import React, { useState, useEffect } from 'react'
import { withRouter } from 'react-router'
import PropTypes from 'prop-types'
import { some } from 'lodash'
import { Box, Button, Typography, Link } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import api from 'common/api'
import useScid from 'common/use-scid'
import { useRoundConfigurationAppCycle } from 'store/application-cycle/hooks'

import useCanManageApplications from '../../../useCanManageApplications'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import PreviewApplicationButton from 'routes/application/Activate/components/PreviewApplicationButton'

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: theme.spacing(68),
  },
  noButton: {
    marginLeft: theme.spacing(2),
  },
}))

export const ConfirmAppRoundsStep = ({ appConfig, completeStep, history }) => {
  const configurationConfirmed = appConfig && appConfig.verification && appConfig.verification.configurationConfirmed
  const transferAppRoundRequired =
    appConfig && appConfig.transferConfiguration && appConfig.transferConfiguration.enabled
  const firstYearAppRoundRequired =
    appConfig && appConfig.firstYearConfiguration && appConfig.firstYearConfiguration.enabled
  const [confriming, setConfirming] = useState(false)
  const [confirmationError, setConfirmationError] = useState(false)
  const [unmappedRoundNames, setUnmappedRoundNames] = useState([])
  const [hasTransferAppRound, setHasTransferAppRound] = useState(false)
  const [hasFirstYearAppRound, setHasFirstYearAppRound] = useState(false)
  const [appRoundsError, setAppRoundsError] = useState(false)
  const styles = useStyles()
  const scid = useScid()
  const canCompleteStep = useCanManageApplications()
  const appCycle = useRoundConfigurationAppCycle()
  useEffect(() => {
    api
      .get(`/api/colleges/${scid}/application-deadlines/${appCycle}`)
      .then((response) => {
        if (response && response.length > 0) {
          setHasTransferAppRound(some(response, (round) => round.ApplicantType === 'transfer'))
          setHasFirstYearAppRound(some(response, (round) => round.ApplicantType === 'firstYear'))
        }
      })
      .catch(() => setAppRoundsError(true))
  }, [scid, appCycle])
  const confirmRoundSelections = () => {
    setConfirming(true)
    api
      .put(
        `/api/colleges/${scid}/integrations/application/configuration/${appConfig?.id}/verification/confirmAppRounds`,
      )
      .then(async () => {
        setConfirmationError(false)
        completeStep()
      })
      .catch(async (error) => {
        setConfirming(false)
        setConfirmationError(true)

        if (error.status === 412) {
          const unmappedRoundsResponse = await error.json()
          setUnmappedRoundNames(unmappedRoundsResponse.unmappedRounds)
        }
      })
  }

  const renderConfirmationError = () => {
    if (unmappedRoundNames.length > 0) {
      return (
        <>
          <Typography
            variant="caption"
            color="error"
          >
            An error occurred while confirming your App Rounds. The following rounds are not mapped in Slate.
            <ul>
              {unmappedRoundNames.map((round, idx) => (
                <li key={`unmapped-round-${idx}`}>{round}</li>
              ))}
            </ul>
          </Typography>
          <Button
            color="primary"
            target="_blank"
            href="https://knowledge.technolutions.net/docs/coalition-source-formats-application-profile"
            startIcon={
              <FontAwesomeIcon
                icon={faInfoCircle}
                style={{ fontSize: 18 }}
              />
            }
          >
            Learn more about round mapping
          </Button>
        </>
      )
    }

    return (
      <Typography
        variant="caption"
        color="error"
      >
        {transferAppRoundRequired &&
          'An error occurred while confirming your App Rounds. Please ensure you have a Transfer Round configured.'}
        {!transferAppRoundRequired && (
          <>
            An error occurred while confirming your App Rounds.
            <Link
              href="https://www.scoir.com/support"
              target="_blank"
              rel="noreferrer"
            >
              Please contact customer support
            </Link>
            .
          </>
        )}
      </Typography>
    )
  }

  return (
    <Box
      px={4}
      py={1}
    >
      <Typography>{'Have you finished setting up your App Rounds?'}</Typography>
      <Box
        mt={1}
        mb={3}
      >
        <Typography variant="caption">
          {
            'By clicking “Yes” you are verifying that you have set up App Rounds in Scoir for all application types you plan to receive through Scoir this cycle and that each App Round is mapped to a corresponding App Round in Slate.'
          }
        </Typography>
      </Box>
      <Button
        color="primary"
        variant="contained"
        disabled={
          !configurationConfirmed ||
          !canCompleteStep ||
          confriming ||
          appRoundsError ||
          (transferAppRoundRequired && !hasTransferAppRound) ||
          (firstYearAppRoundRequired && !hasFirstYearAppRound)
        }
        onClick={confirmRoundSelections}
      >
        Yes
      </Button>
      <Button
        variant="outlined"
        color="primary"
        className={styles.noButton}
        disabled={!canCompleteStep}
        onClick={() => history.push(`${import.meta.env.BASE_URL}/application/rounds`)}
      >
        No, Go to Set Rounds&nbsp;
        <FontAwesomeIcon icon={faArrowRight} />
      </Button>
      {hasTransferAppRound || (hasFirstYearAppRound && <PreviewApplicationButton configurationId={appConfig.id} />)}
      {firstYearAppRoundRequired && !hasFirstYearAppRound && (
        <Box mt={2}>
          <Typography
            variant="caption"
            color="error"
          >
            To accept first-year applications, you must support at least one first-year round. Please return to your
            Application Rounds and create or assign a first-year round.
          </Typography>
        </Box>
      )}
      {transferAppRoundRequired && !hasTransferAppRound && (
        <Box mt={2}>
          <Typography
            variant="caption"
            color="error"
          >
            To accept transfer applications, you must support at least one transfer round. Please return to your
            Application Rounds and create or assign a transfer round.
          </Typography>
        </Box>
      )}
      {appRoundsError && (
        <Box mt={2}>
          <Typography
            variant="caption"
            color="error"
          >
            An error occurred while retrieving your App Rounds.{' '}
            <Link
              href="https://www.scoir.com/support"
              target="_blank"
              rel="noreferrer"
            >
              Please contact customer support.
            </Link>
          </Typography>
        </Box>
      )}
      {confirmationError && <Box mt={2}>{renderConfirmationError()}</Box>}
    </Box>
  )
}

ConfirmAppRoundsStep.propTypes = {
  appConfig: PropTypes.object,
  completeStep: PropTypes.func,
  history: PropTypes.any,
}

export default withRouter(ConfirmAppRoundsStep)
